import * as S from './styles'
import { useContext, useEffect, useState } from 'react'
import { MainContext } from '../../../context/MainContext/MainContext'
import { PlantContext } from '../../../context/PlantContext/PlantContext'
import { PlantType } from "../../../interfaces/Plant"
import { MoonPhaseLegend, PointDetails } from '../../Molecules'
import { convertToBRDate } from "../../../utils/convertToBRDate"
import updatePlant from '../../../hooks/use-update-plant'
import { AddToCalendarButton } from 'add-to-calendar-button-react'
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist'

type PlantDetailsType = {
  plant: PlantType
}

const PlantDetails = (props: PlantDetailsType) => {
  const { plant } = props
  const { setIsPageLoading } = useContext(MainContext)
  const { setSelectedPlant, setIsPlantUpdated, _plantRefetch } = useContext(PlantContext)

  const [temp, setTemp] = useState<any>(null)
  const [ur, setUR] = useState<any>(null)
  const [vpd, setVpd] = useState<number>(0)
  const [asideTab, setAsideTab] = useState<string>("details")

  /**
   * Calculate start bloom date
   */
  let plant_start = new Date(plant.plant_start_vega)
  let plant_bloom = plant_start.setDate(plant_start.getDate() + plant.plant_cycle_vega)

  const plant_start_vega = new Date(plant.plant_start_vega)
  const plant_start_bloom = new Date(plant_bloom)
  const totalDays = plant.plant_cycle_vega + plant.plant_cycle_bloom
  const plant_start_time = new Date(plant.plant_start_vega)
  const plant_crop_time = plant_start_time.setDate(plant_start_time.getDate() + (totalDays - 1))
  const plant_crop_date = new Date(plant_crop_time)
  const today = new Date()
  const oneDay = 86400000
  const daysToEnd = Math.ceil((plant_crop_date.getTime() - today.getTime()) / oneDay)



  const handleUpdatePlant = (ev: any) => {
    ev.preventDefault()
    setIsPageLoading(true)

    var data = new FormData(ev.target)
    let plantForm: any = {}
    data.forEach((d, i) => {
      if (i !== 'plant_temp' && i !== 'plant_ur') {
        plantForm[i] = d
      }
    })

    updatePlant(plantForm, plant.plant_id).then((response) => {
      if (response.data?.length) {
        _plantRefetch().then(() => {
          setIsPlantUpdated(true)
          setSelectedPlant(plant)
        })
        setIsPageLoading(false)
      }
    })
  }

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(function (position) {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;
      console.log(`Latitude: ${latitude}, Longitude: ${longitude}`);
    }, function () {
      console.log("Unable to retrieve your location");
    })
  }

  const vpdVegaTable = [
    { week: '1ª Semana de Vegetativo', vpd: { min: 0.6, max: 0.9 }, description: 'Primeiras folhas, ainda sensível' },
    { week: '2ª Semana de Vegetativo', vpd: { min: 0.8, max: 1.0 }, description: 'Começa a se alimentar bem, maior transpiração' },
    { week: '3ª Semana de Vegetativo', vpd: { min: 0.9, max: 1.1 }, description: 'Estica, cria massa verde' },
    { week: '4ª Semana de Vegetativo', vpd: { min: 0.9, max: 1.1 }, description: 'Estica, cria massa verde' },
    { week: '5ª Semana de Vegetativo', vpd: { min: 1.0, max: 1.2 }, description: 'Planta já “forte” e preparada para a floração' },
    { week: '6ª Semana de Vegetativo', vpd: { min: 1.0, max: 1.2 }, description: 'Planta já “forte” e preparada para a floração' },
    { week: '7ª Semana de Vegetativo', vpd: { min: 1.0, max: 1.2 }, description: 'Planta já “forte” e preparada para a floração' },
    { week: '8ª Semana de Vegetativo', vpd: { min: 1.0, max: 1.2 }, description: 'Planta já “forte” e preparada para a floração' },
    { week: '9ª Semana de Vegetativo', vpd: { min: 1.0, max: 1.2 }, description: 'Planta já “forte” e preparada para a floração' },
    { week: '10ª Semana de Vegetativo', vpd: { min: 1.0, max: 1.2 }, description: 'Planta já “forte” e preparada para a floração' },
    { week: '11ª Semana de Vegetativo', vpd: { min: 1.0, max: 1.2 }, description: 'Planta já “forte” e preparada para a floração' },
    { week: '12ª Semana de Vegetativo', vpd: { min: 1.0, max: 1.2 }, description: 'Planta já “forte” e preparada para a floração' }
  ]

  const vpdBloomTable = [
    { week: '1ª Semana de Floração (última de vega)', vpd: { min: 0.8, max: 1.2 }, description: 'Planta ainda se estica e forma ramos' },
    { week: '2ª Semana de Floração (pré-flora)', vpd: { min: 1.2, max: 1.4 }, description: 'Pico de alongamento e início de flores' },
    { week: '3ª Semana de Floração (formação dos botões)', vpd: { min: 1.2, max: 1.4 }, description: 'Pico de alongamento e início de flores' },
    { week: '4ª Semana de Floração (formação dos botões)', vpd: { min: 1.4, max: 1.6 }, description: 'Formação e enchimento das flores' },
    { week: '5ª Semana de Floração (engorda e resina)', vpd: { min: 1.4, max: 1.6 }, description: 'Formação e enchimento das flores' },
    { week: '6ª Semana de Floração (engorda e resina)', vpd: { min: 1.4, max: 1.6 }, description: 'Formação e enchimento das flores' },
    { week: '7ª Semana de Floração (maturação)', vpd: { min: 1.5, max: 1.8 }, description: 'Reduz a umidade para evitar mofo e estimular produção de resina' },
    { week: '8ª Semana de Floração (maturação)', vpd: { min: 1.5, max: 1.8 }, description: 'Reduz a umidade para evitar mofo e estimular produção de resina' },
    { week: '9ª Semana de Floração (maturação)', vpd: { min: 1.5, max: 1.8 }, description: 'Reduz a umidade para evitar mofo e estimular produção de resina' },
    { week: '10ª Semana de Floração (maturação)', vpd: { min: 1.5, max: 1.8 }, description: 'Reduz a umidade para evitar mofo e estimular produção de resina' },
    { week: '11ª Semana de Floração (maturação)', vpd: { min: 1.5, max: 1.8 }, description: 'Reduz a umidade para evitar mofo e estimular produção de resina' },
    { week: '12ª Semana de Floração (maturação)', vpd: { min: 1.5, max: 1.8 }, description: 'Reduz a umidade para evitar mofo e estimular produção de resina' }
  ]

  const rest = plant.plant_cycle_bloom - daysToEnd
  const calcVega = Math.ceil((plant.plant_cycle_vega + rest) / 7) - 1
  const calcBloom = Math.ceil(rest / 7) - 1

  const currentPeriod = (plant.plant_cycle_vega + rest < plant.plant_cycle_vega) ? {
    period: 'Vega',
    weekNum: Math.ceil((plant.plant_cycle_vega + rest) / 7),
    vpdTable: vpdVegaTable[calcVega < 0 ? 0 : calcVega]
  } : {
    period: 'Flora',
    weekNum: Math.ceil(rest / 7),
    vpdTable: vpdBloomTable[calcBloom < 0 ? 0 : calcBloom]
  }

  useEffect(() => {
    setVpd(parseFloat(calcularVPD(temp, ur)))
  }, [temp, ur, setVpd])

  const handleTemp = (t: any) => {
    setTemp(parseInt(t.target.value))
  }

  const handleUR = (u: any) => {
    setUR(parseInt(u.target.value))
  }

  const calcularVPD = (temperaturaC: number, umidadeRelativa: number) => {
    const vp_sat = 0.6108 * Math.exp((17.27 * temperaturaC) / (temperaturaC + 237.3));
    const vpd = vp_sat * (1 - umidadeRelativa / 100);
    return vpd.toFixed(2);
  }

  const handleChangeTab = (tab: string) => {
    setAsideTab(tab)
  }

  return (
    <>
      <S.AsideFormStyled onSubmit={handleUpdatePlant}>
        <h2><input defaultValue={plant.plant_strain} name='plant_strain' type='text' className='title' /></h2>

        {
          ((plant.plant_cycle_bloom - daysToEnd) >= 15 && (plant.plant_cycle_bloom - daysToEnd) <= 21) ?
            <div className='flag'>
              <span><strong>3<sup>a</sup></strong><br />sem.</span>
              <BookmarkIcon />
            </div> :
            <></>
        }

        {
          ((plant.plant_cycle_bloom - daysToEnd) >= 36 && (plant.plant_cycle_bloom - daysToEnd) <= 42) ?
            <div className='flag'>
              <span><strong>6<sup>a</sup></strong><br />sem.</span>
              <BookmarkIcon />
            </div> :
            <></>
        }

        {
          ((plant.plant_cycle_bloom - daysToEnd) >= 43 && daysToEnd < 11 && daysToEnd > 1) ?
            <div className='flag'>
              <span>Faltam<br /><strong>{daysToEnd}</strong><br />dias</span>
              <BookmarkIcon />
            </div> :
            <></>
        }

        {
          (daysToEnd === 1) ?
            <div className='flag'>
              <span>Falta<br /><strong>{daysToEnd}</strong><br />dia</span>
              <BookmarkIcon />
            </div> :
            <></>
        }

        {
          (daysToEnd === 0) ?
            <div className='flag'>
              <span><LocalFloristIcon /></span>
              <BookmarkIcon />
            </div> :
            <></>
        }

        <nav className='aside-tabs'>
          <button type='button' onClick={() => handleChangeTab('details')} className={asideTab === 'details' ? 'active' : ''}>Infos</button>
          <button type='button' onClick={() => handleChangeTab('vpd')} className={asideTab === 'vpd' ? 'active' : ''}>VPD (kPa)</button>
        </nav>

        <div hidden={asideTab !== 'vpd'}>
          <div className='vpd'>
            <p>Você está na {currentPeriod?.vpdTable?.week}.</p>
            <p>O VPD ideal para esse período é: <strong>{currentPeriod?.vpdTable?.vpd.min} a {currentPeriod?.vpdTable?.vpd.max}</strong> kPa.</p>

            <h3>Calcule seu VPD</h3>
            <div className='vpd-calculator'>
              <div><span className='label'>Temperatura (°C):</span><span className='input double'> <input type='number' name='plant_temp' defaultValue={temp} onChange={(ev) => handleTemp(ev)} /></span></div>
              <div><span className='label'>Umidade Rel. (%):</span><span className='input double'> <input type='number' name='plant_ur' defaultValue={ur} onChange={(ev) => handleUR(ev)} /></span></div>
              <div><span className='label'>Seu VPD está em:</span><span className='input double'><strong>{vpd} kPa</strong></span></div>
            </div>

            {
              (temp && ur) ?
                <div className='vpd-result'>
                  {
                    vpd < currentPeriod?.vpdTable?.vpd.min ?
                      <p className='down'>Seu VPD está abaixo do ideal. Abaixe a umidade ou aumente a temperatura para regular o VPD.</p> :
                      <></>
                  }

                  {
                    vpd > currentPeriod?.vpdTable?.vpd.max ?
                      <p className='up'>Seu VPD está acima do ideal. Aumente a umidade ou diminua a temperatura para regular o VPD.</p> :
                      <></>
                  }

                  {
                    vpd < currentPeriod?.vpdTable?.vpd.max && vpd > currentPeriod?.vpdTable?.vpd.min ?
                      <p>Seu VPD está dentro do ideal. Continue assim.</p> :
                      <></>
                  }
                </div> :
                <div><sub><i>* Preencha os campos acima para calcular o VPD.</i></sub></div>
            }

          </div>
        </div>
        <div hidden={asideTab !== 'details'}>
          <div className='details'>
            <div><span className='label'>Quantidade:</span><span className='input double'> <input defaultValue={plant.plant_qty} name='plant_qty' type='number' /></span></div>
            <div><span className='label'>Seedbank:</span><span className='input'> <input defaultValue={plant.plant_seedbank} name='plant_seedbank' type='text' /></span></div>
            <div>
              <span className='label'>Tipo:</span>
              <span className='input'>
                <select name='plant_type' defaultValue={plant.plant_type}>
                  <option value='photo_fem'>Fotoperíodo</option>
                  <option value='auto_fem'>Automática</option>
                  <option value='photo_reg'>Regular</option>
                </select>
              </span>
            </div>

            <div><span className='label'>Início da vega:</span><span> <input defaultValue={convertToBRDate(plant_start_vega, 'input')} name='plant_start_vega' type='date' /></span></div>
            <div><span className='label'>Tempo de vega:</span><span className='input double'> <input defaultValue={plant.plant_cycle_vega} name='plant_cycle_vega' type='number' /> dias</span></div>
            <div><span className='label'>Início da flora:</span><span className='value'> {convertToBRDate(plant_start_bloom, 'full')}</span></div>
            <div><span className='label'>Tempo de flora:</span><span className='input double'> <input defaultValue={plant.plant_cycle_bloom} name='plant_cycle_bloom' type='number' /> dias</span></div>
            <div><span className='label'>Colheita em:</span><span className='value'> {convertToBRDate(plant_crop_date, 'full')}</span></div>

            <AddToCalendarButton
              name={`Colheita da ${plant.plant_strain}`}
              description={plant.plant_details}
              startDate={convertToBRDate(plant_crop_date, 'input')}
              options={['Google']}
              timeZone="America/Sao_Paulo"
              location="São Paulo, Brasil"
              label="Adicionar colheita ao calendário"
              hideIconButton={true}
              size='2'
            ></AddToCalendarButton>

            <textarea defaultValue={plant.plant_details} name='plant_details'></textarea>

            <button type='submit'>Salvar</button>
          </div>
        </div>

      </S.AsideFormStyled >
      <PointDetails />
      <MoonPhaseLegend />
    </>
  )
}

export default PlantDetails
